// Serialización pura de steps de workflow para el request al backend.
// Se mantiene libre de dependencias pesadas (sin axios/servicios) para poder
// testearla de forma aislada — el round-trip "snapshot in -> snapshot out" es la
// garantía clave contra el bug de corrupción al re-guardar desde la UI.

const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isValidUUID = (uuid) => typeof uuid === 'string' && UUID_REGEX.test(uuid);

export const RESERVED_STEP_KEYS = new Set([
  'id',
  'workflow_id',
  'name',
  'description',
  'step_order',
  'parameters',
  'dependencies',
  'timeout_seconds',
  'conditions',
  'parallel_group',
  'is_critical',
  'step_metadata',
  'step_type',
  'assistant_id',
  'tasks',
  'position',
  // Metadatos internos de serialización, nunca se envían al backend.
  '_rawStep',
]);

export const normalizeWorkflowStepForRequest = (step, index, workflowId) => {
  const source = isPlainObject(step) ? step : {};
  // Snapshot original del step (passthrough). Garantiza un round-trip sin pérdida:
  // los campos que la UI no edita se conservan tal cual venían del backend.
  const rawStep = isPlainObject(source._rawStep) ? source._rawStep : {};
  const rawStepParams = isPlainObject(rawStep.parameters) ? rawStep.parameters : {};

  const uiParameters = isPlainObject(source.parameters) ? source.parameters : {};
  // Base = parameters del snapshot, sobreescritos por los parameters que la UI sí maneja.
  const parameters = { ...rawStepParams, ...uiParameters };

  if (typeof source.step_type === 'string' && source.step_type.trim()) {
    parameters.step_type = source.step_type.trim();
  }

  if (source.assistant_id && !parameters.assistant_id) {
    parameters.assistant_id = source.assistant_id;
  }

  // Algunas ramas (ai_agent) aplanan la configuración a la raíz; copiar esos
  // parameters-level keys, sobreescribiendo la base de passthrough con lo editado.
  Object.entries(source).forEach(([key, value]) => {
    if (RESERVED_STEP_KEYS.has(key)) return;
    if (value !== undefined) {
      parameters[key] = value;
    }
  });

  const stepId = isValidUUID(source.id) ? source.id : null;

  if (Array.isArray(source.tasks) && source.tasks.length > 0) {
    parameters.tasks = source.tasks;
  }

  const position = source.position && typeof source.position.x === 'number' && typeof source.position.y === 'number'
    ? { x: source.position.x, y: source.position.y }
    : null;

  // Campos de nivel step: preservar del snapshot original salvo que la UI los
  // provea explícitamente. El `timeout_seconds` de nivel step (timeout de ejecución)
  // NO debe confundirse con el `parameters.timeout_seconds` (duración de un wait).
  return {
    ...(stepId ? { id: stepId } : {}),
    workflow_id: workflowId,
    name: (typeof source.name === 'string' && source.name.trim()) || `Step ${index + 1}`,
    description: typeof source.description === 'string' ? source.description : '',
    step_order: Number.isInteger(source.step_order)
      ? source.step_order
      : Number.isInteger(rawStep.step_order)
        ? rawStep.step_order
        : index,
    parameters,
    dependencies: Array.isArray(source.dependencies)
      ? source.dependencies
      : Array.isArray(rawStep.dependencies)
        ? rawStep.dependencies
        : [],
    timeout_seconds: Number.isFinite(source.timeout_seconds)
      ? source.timeout_seconds
      : Number.isFinite(rawStep.timeout_seconds)
        ? rawStep.timeout_seconds
        : 300,
    conditions: source.conditions ?? rawStep.conditions ?? null,
    parallel_group: source.parallel_group ?? rawStep.parallel_group ?? null,
    is_critical: typeof source.is_critical === 'boolean'
      ? source.is_critical
      : typeof rawStep.is_critical === 'boolean'
        ? rawStep.is_critical
        : true,
    step_metadata: isPlainObject(source.step_metadata)
      ? source.step_metadata
      : isPlainObject(rawStep.step_metadata)
        ? rawStep.step_metadata
        : {},
    ...(position ? { position } : {})
  };
};
